import { useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useQueryClient } from '@tanstack/react-query';

const ACTIVITY_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Single source of truth for user presence on Forja Hub.
 *
 * Calls update_user_activity RPC every 5 minutes (and on tab focus), which:
 *   1. Updates profiles.last_seen_at (drives "Acessos Hoje" + "Membros Ativos")
 *   2. Applies the daily Sparks grant once per day (idempotent server-side)
 *
 * If the RPC reports a fresh grant, we invalidate the wallet cache so the
 * UI reflects the new balance immediately.
 */
export function useUserActivity() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastUpdateRef = useRef<number>(0);

  const updateActivity = useCallback(async () => {
    // Debounce: skip if updated less than 30s ago
    const now = Date.now();
    if (now - lastUpdateRef.current < 30_000) return;
    lastUpdateRef.current = now;

    try {
      const { data } = await supabase.rpc('update_user_activity');
      const granted = (data as { daily_granted?: boolean } | null)?.daily_granted;
      if (granted && user?.id) {
        queryClient.invalidateQueries({ queryKey: ['sparks-wallet', user.id] });
      }
    } catch {
      // Silent fail - non-critical
    }
  }, [queryClient, user?.id]);

  useEffect(() => {
    if (!user) return;

    // Update immediately on mount
    updateActivity();

    // Then every 5 minutes
    intervalRef.current = setInterval(updateActivity, ACTIVITY_INTERVAL_MS);

    // Also update when user returns to the tab
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        updateActivity();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user?.id, updateActivity]);
}
